import { Check, Copy01 } from '@untitledui/icons';
import { useCallback } from 'react';

import { useClipboard } from '@/hooks/useClipboard';
import { Button } from '@/lib/base/buttons/button';
import { getApiBaseUrl } from '@/lib/http/client';

/**
 * Canonical path verified via the one-time-secret modal's copyable curl
 * snippet. Trailing slash included: the public API 308s the slash-less form
 * to this one, and `curl` without `-L` would show the redirect, not the body.
 */
export const TOKEN_VERIFY_PATH = '/rest/v1/self/';

/**
 * Copyable curl snippet verifying a freshly minted service account token against
 * GET /rest/v1/self/.
 *
 * Built on `getApiBaseUrl()`, NOT `window.location.origin`: the SPA and the
 * API are served from different hosts (app.* vs api.*), so a snippet pointed
 * at the page's own origin never reaches the public API.
 */
export function TokenSelfCheckInstruction() {
  const { copied, copy } = useClipboard();
  const isCopied = copied === 'self-check-command';
  const curlSnippet = `curl -H "Authorization: Bearer <token>" ${getApiBaseUrl()}${TOKEN_VERIFY_PATH}`;

  const handleCopy = useCallback(() => {
    void copy(curlSnippet, 'self-check-command');
  }, [copy, curlSnippet]);

  return (
    <>
      Verify this token with GET {TOKEN_VERIFY_PATH}.
      <br />
      <span className='mt-2 inline-flex items-center gap-2'>
        <code className='rounded-md bg-[var(--color-bg-secondary)] px-2 py-1 font-mono text-xs text-[var(--color-text-primary)]'>
          {curlSnippet}
        </code>
        <Button
          color='secondary'
          size='sm'
          iconLeading={isCopied ? Check : Copy01}
          onClick={handleCopy}
          aria-label={isCopied ? 'Command copied' : 'Copy command'}
        >
          {isCopied ? 'Copied' : 'Copy'}
        </Button>
      </span>
    </>
  );
}
